/**
 * Clicky Text rules shared by the API worker and the frontend.
 *
 * A clicky text is a condition snippet for the Testing notes box. Each person
 * has their own list. There is also one shared list, the Everyone list, that
 * only admins change and every tester sees. Its scope says which items it
 * shows on. Categories come only from the master list in shared/categories,
 * never from a copy.
 */

import { CATEGORY_ORDER, PRODUCT_TYPES, getMainCategories } from './categories/index.js';

export const CLICKY_SCOPES = ['global', 'main', 'sub'];
export const MAX_SHORT_FORM = 40;
export const MAX_LONG_FORM = 1000;

/**
 * The owner value (clicky_text.user_id) of the Everyone list. Real user ids are
 * numbers, so this can never clash with a person's list.
 */
export const EVERYONE_LIST = 'everyone';

/**
 * Check and tidy a clicky text from a request body.
 * @param {{scope?: string, category?: string|null, shortForm?: string, longForm?: string}} body
 * @returns {{value: {scope: string, category: string|null, shortForm: string, longForm: string}} | {error: string}}
 */
export function parseClickyText(body) {
    const scope = body?.scope;
    if (!CLICKY_SCOPES.includes(scope)) return { error: 'Type must be Global, Main Category or Sub Category' };

    const category = scope === 'global' ? null : String(body.category ?? '').trim();
    const categoryError = checkCategory(scope, category);
    if (categoryError) return { error: categoryError };

    const shortForm = String(body.shortForm ?? '').trim();
    const longForm = String(body.longForm ?? '').trim();
    if (!shortForm) return { error: 'Short form is required' };
    if (!longForm) return { error: 'Long form is required' };
    if (shortForm.length > MAX_SHORT_FORM) return { error: `Short form must be ${MAX_SHORT_FORM} characters or fewer` };
    if (longForm.length > MAX_LONG_FORM) return { error: `Long form must be ${MAX_LONG_FORM} characters or fewer` };

    return { value: { scope, category, shortForm, longForm } };
}

/**
 * Does this clicky text belong on an item of this sub category?
 * Global always fits. A sub that sits under two mains fits rows for either main.
 * @param {{scope: string, category: string|null}} row
 * @param {string|null|undefined} itemSub
 */
export function clickyTextFitsItem(row, itemSub) {
    if (row.scope === 'global') return true;
    if (!itemSub) return false;
    if (row.scope === 'sub') return row.category === itemSub;
    return getMainCategories(itemSub).includes(row.category);
}

/**
 * The rows for the Testing notes box on one item: the Everyone rows that fit
 * first, then your own that fit, each list in its own order. Your own row is
 * left out only when it repeats an Everyone row word for word (same short and
 * long form), because then it adds nothing. A row that shares a short form but
 * has other wording stays, so a tester never loses their own text.
 * @template {{scope: string, category: string|null, short_form: string, long_form: string}} Row
 * @param {Row[]} everyoneRows the Everyone list, in order
 * @param {Row[]} ownRows the tester's own list, in order
 * @param {string|null|undefined} itemSub
 * @returns {Row[]}
 */
export function clickyTextForItem(everyoneRows, ownRows, itemSub) {
    const shared = everyoneRows.filter((row) => clickyTextFitsItem(row, itemSub));
    const seen = new Set(shared.map(wordingKey));
    const own = ownRows.filter((row) => clickyTextFitsItem(row, itemSub) && !seen.has(wordingKey(row)));
    return [...shared, ...own];
}

/**
 * Move one id to a new 1-based position and return the full new order.
 * @param {number[]} ids current order
 * @param {number} id the row to move
 * @param {number} position wanted 1-based position, clamped to the list
 */
export function moveToPosition(ids, id, position) {
    const rest = ids.filter((other) => other !== id);
    const index = Math.min(Math.max(Math.trunc(position) - 1, 0), rest.length);
    return [...rest.slice(0, index), id, ...rest.slice(index)];
}

function wordingKey(row) {
    return JSON.stringify([row.short_form.trim(), row.long_form.trim()]);
}

function checkCategory(scope, category) {
    if (scope === 'main' && !CATEGORY_ORDER.includes(category)) return 'Pick a main category from the list';
    if (scope === 'sub' && !PRODUCT_TYPES.includes(category)) return 'Pick a sub category from the list';
    return null;
}
